/**
 * Content analytics. Two sinks:
 * - first-party: batched to the Universe server (`POST /api/universe/v1/events`),
 *   drained with sendBeacon on pagehide; on unless VITE_UNIVERSE_EVENTS=off;
 * - the Ciright tracker (Fishing tracker v2, `window.Fishing`) when
 *   VITE_FISHING_SITE_ID is set; it feeds analytics.ciright.com → Fishing attribution.
 * Inventory: docs/interaction-coverage.md.
 *
 * Rules enforced here:
 * - never send OTPs, passwords, tokens, or personal form values;
 * - props are flat scalars (the tracker drops anything else);
 * - analytics can never throw into reading, playback, or sorting;
 * - nothing is invented: unknown values are sent as 'unknown', not zero.
 */
import { CIRIGHT_AUTH } from './auth';
import { API_BASE } from './config';

export const ANALYTICS_SCHEMA_VERSION = 1;

const SITE_ID = String(import.meta.env.VITE_FISHING_SITE_ID ?? '').trim();
const FIRST_PARTY = String(import.meta.env.VITE_UNIVERSE_EVENTS ?? '').toLowerCase() !== 'off';
const EVENTS_URL = `${API_BASE}/api/universe/v1/events`;
const FLUSH_AT = 20;
const FLUSH_MS = 5000;
const TRACKER_ORIGIN = String(import.meta.env.VITE_FISHING_TRACKER_URL ?? 'https://cdn.fishing.ciright.com')
  .trim()
  .replace(/\/$/, '');

type Scalar = string | number | boolean;
export type EventProps = Record<string, Scalar | null | undefined>;

interface FishingApi {
  track?: (name: string, props: Record<string, Scalar>) => void;
  identify?: (userId: string, traits?: Record<string, Scalar>) => void;
  page?: () => void;
  q?: unknown[][];
}

declare global {
  interface Window {
    Fishing?: FishingApi;
    __universeAnalytics?: { health: () => AnalyticsHealth };
  }
}

export interface AnalyticsHealth {
  status: 'active' | 'not_configured' | 'script_failed';
  sent: number;
  droppedInvalid: number;
  droppedNotConfigured: number;
  lastEventAt: string | null;
  firstParty: { enabled: boolean; delivered: number; failed: number; queued: number };
  /** Fishing's retries, rejections and beacon drains happen inside its tracker and aren't observable here. */
  deliveryVisibility: 'limited';
}

const health: AnalyticsHealth = {
  status: SITE_ID || FIRST_PARTY ? 'active' : 'not_configured',
  sent: 0,
  droppedInvalid: 0,
  droppedNotConfigured: 0,
  lastEventAt: null,
  firstParty: { enabled: FIRST_PARTY, delivered: 0, failed: 0, queued: 0 },
  deliveryVisibility: 'limited',
};

interface QueuedEvent {
  name: string;
  ev_id: string;
  client_ts: string;
  props: Record<string, Scalar>;
}

const queue: QueuedEvent[] = [];
let flushTimer: number | null = null;

function flush(useBeacon = false) {
  if (flushTimer !== null) {
    window.clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (!queue.length) return;
  const batch = queue.splice(0, 50);
  health.firstParty.queued = queue.length;
  const body = JSON.stringify({ events: batch });
  if (useBeacon && typeof navigator.sendBeacon === 'function') {
    const queued = navigator.sendBeacon(EVENTS_URL, new Blob([body], { type: 'application/json' }));
    if (queued) health.firstParty.delivered += batch.length;
    else health.firstParty.failed += batch.length;
  } else {
    fetch(EVENTS_URL, { method: 'POST', credentials: 'include', keepalive: true, headers: { 'Content-Type': 'application/json' }, body })
      .then((res) => {
        if (res.ok) health.firstParty.delivered += batch.length;
        else health.firstParty.failed += batch.length;
      })
      .catch(() => {
        health.firstParty.failed += batch.length;
      });
  }
  if (queue.length) flush(useBeacon);
}

function enqueue(event: QueuedEvent) {
  queue.push(event);
  health.firstParty.queued = queue.length;
  if (queue.length >= FLUSH_AT) flush();
  else if (flushTimer === null) flushTimer = window.setTimeout(() => flush(), FLUSH_MS);
}

export function getAnalyticsHealth(): AnalyticsHealth {
  return { ...health };
}

const NAME = /^[a-z0-9_]{1,64}$/;
/** Belt and braces: the tracker strips some of these too, but personal values must never leave. */
const SENSITIVE_KEY = /pass|otp|token|secret|auth|cookie|email|phone|mobile|msisdn|card|cvv|ssn|full_?name|username/i;

function uuid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  }
}

function sanitize(props: EventProps): Record<string, Scalar> {
  const out: Record<string, Scalar> = {};
  for (const [key, value] of Object.entries(props)) {
    if (value === null || value === undefined || SENSITIVE_KEY.test(key)) continue;
    if (typeof value === 'number' && !Number.isFinite(value)) continue;
    out[key] = typeof value === 'string' ? value.slice(0, 256) : value;
  }
  return out;
}

function trafficClass(): string {
  try {
    if (navigator.webdriver) return 'automation';
    if (new URLSearchParams(window.location.search).has('internal')) return 'internal';
  } catch {
    /* ignore */
  }
  return 'unclassified';
}

let installed = false;

export function initAnalytics() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.__universeAnalytics = { health: getAnalyticsHealth };
  if (FIRST_PARTY) {
    window.addEventListener('pagehide', () => flush(true));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush(true);
    });
  }
  if (!SITE_ID) return;
  // Queue stub from docs/tracker-install.md: calls made before the tracker loads are replayed.
  const fishing: FishingApi = window.Fishing ?? {};
  fishing.q = fishing.q ?? [];
  for (const method of ['track', 'identify', 'page', 'consent'] as const) {
    const existing = (fishing as Record<string, unknown>)[method];
    if (typeof existing !== 'function') {
      (fishing as Record<string, unknown>)[method] = (...args: unknown[]) => fishing.q?.push([method, ...args]);
    }
  }
  window.Fishing = fishing;
  const script = document.createElement('script');
  script.async = true;
  script.src = `${TRACKER_ORIGIN}/tracker.js`;
  script.dataset.siteId = SITE_ID;
  script.onerror = () => {
    health.status = 'script_failed';
  };
  document.head.appendChild(script);
}

let routeSeq = 0;

/** SPA navigation: the tracker records the page view itself. */
export function trackPage() {
  routeSeq += 1;
  try {
    window.Fishing?.page?.();
  } catch {
    /* never break navigation */
  }
}

export function track(name: string, props: EventProps = {}) {
  try {
    if (!NAME.test(name)) {
      health.droppedInvalid += 1;
      return;
    }
    if (!SITE_ID && !FIRST_PARTY) {
      health.droppedNotConfigured += 1;
      return;
    }
    const evId = uuid();
    const clientTs = new Date().toISOString();
    const payload = sanitize({
      schema_v: ANALYTICS_SCHEMA_VERSION,
      ev_id: evId,
      client_ts: clientTs,
      app_id: CIRIGHT_AUTH.appId,
      app_subscription_id: CIRIGHT_AUTH.subscriptionId,
      route_seq: routeSeq,
      traffic: trafficClass(),
      visibility: document.visibilityState,
      ...props,
    });
    if (SITE_ID) window.Fishing?.track?.(name, payload);
    if (FIRST_PARTY) enqueue({ name, ev_id: evId, client_ts: clientTs, props: payload });
    health.sent += 1;
    health.lastEventAt = payload.client_ts as string;
  } catch {
    /* analytics must never interrupt the experience */
  }
}

/** Authorized identity transition: only the canonical UID, never contact details. */
export function identify(uid: string | null | undefined) {
  if (!uid || !SITE_ID) return;
  try {
    window.Fishing?.identify?.(uid, { source_application: 'universe' });
  } catch {
    /* ignore */
  }
}

export interface ContentRef {
  id: string;
  type: string;
  version?: string | null;
  ownerWorld?: string | null;
  thumbnailVariant?: string | null;
}

/** Common content fields; missing ones are reported as unknown rather than guessed. */
export function contentProps(c: ContentRef): EventProps {
  return {
    content_id: c.id,
    content_type: c.type,
    content_version: c.version ?? 'unknown',
    content_owner_world: c.ownerWorld ?? 'unknown',
    thumbnail_variant: c.thumbnailVariant ?? 'unknown',
  };
}

export function assetContent(asset: {
  id: string;
  type: string;
  content_version?: string | null;
  owner_world?: string | null;
  thumbnail_variant?: string | null;
}): ContentRef {
  return {
    id: asset.id,
    type: asset.type,
    version: asset.content_version,
    ownerWorld: asset.owner_world,
    thumbnailVariant: asset.thumbnail_variant,
  };
}

export function newInstanceId(): string {
  return uuid();
}
